/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import * as constants from '../../../../common/constants';

/** Path segments must be literal keys — no slashes, query chars, or encoding tricks. */
const SAFE_TRENDS_EXPORT_PATH_SEGMENT = /^[A-Za-z0-9_-]+$/;

const TRENDS_FEEDBACK_VALUES = Object.freeze(['LIKE', 'DISLIKE']);
const TRENDS_BLOCKED_CATEGORIES = Object.freeze(['SelfHarm', 'Hate', 'Violence', 'Sexual', 'BlockWords']);

const isAllowedPathParam = (value, allowlist) => {
  if (value == null) {
    return false;
  }

  const normalized = String(value).trim();

  if (!SAFE_TRENDS_EXPORT_PATH_SEGMENT.test(normalized)) {
    return false;
  }

  return !allowlist?.length || allowlist.includes(normalized);
};

/** Single source of truth for audit export module metadata (events, status stores, endpoints). */
export const AUDIT_EXPORT_MODULE_CONFIG = Object.freeze({
  observe: {
    event: constants.GET_OBSERVE_EXPORT_DATA,
    statusStore: constants.OBSERVE_EXPORT_STATUS,
    endpoint: constants.OBSERVE_EXPORT_ENDPOINT,
    filenameSlug: 'observe',
    /** OTEL-aware Observe tenants use v3 merged export; others keep v2 Observe-only export. */
    resolveClients: (event) =>
      event?.cursorPaging ? constants.AUDIT_EXPORT_V3_CLIENTS : constants.AUDIT_EXPORT_DEFAULT_CLIENTS
  },
  feedback: {
    event: constants.GET_FEEDBACK_EXPORT_DATA,
    statusStore: constants.FEEDBACK_EXPORT_STATUS,
    endpoint: constants.FEEDBACK_EXPORT_ENDPOINT,
    filenameSlug: 'feedback'
  },
  trendsCluster: {
    event: constants.GET_TRENDS_CLUSTERS_EXPORT_DATA,
    statusStore: constants.TRENDS_CLUSTERS_EXPORT_STATUS,
    endpoint: constants.TRENDS_CLUSTERS_EXPORT_ENDPOINT,
    filenameSlug: 'trends-cluster'
  },
  trendsCount: {
    event: constants.GET_TRENDS_COUNT_EXPORT_DATA,
    statusStore: constants.TRENDS_COUNT_EXPORT_STATUS,
    endpoint: constants.TRENDS_COUNT_EXPORT_ENDPOINT,
    filenameSlug: 'trends-count'
  },
  otelTrendsCount: {
    event: constants.GET_OTEL_TRENDS_COUNT_EXPORT_DATA,
    statusStore: constants.OTEL_TRENDS_COUNT_EXPORT_STATUS,
    endpoint: constants.OTEL_TRENDS_COUNT_EXPORT_ENDPOINT,
    filenameSlug: 'otel-trends-count',
    clients: constants.AUDIT_EXPORT_V1_CLIENTS
  },
  titleSearch: {
    event: constants.GET_SEARCH_LIST_EXPORT_DATA,
    statusStore: constants.SEARCH_LIST_EXPORT_STATUS,
    endpoint: constants.SEARCH_LIST_EXPORT_ENDPOINT,
    filenameSlug: 'search-list'
  },
  trendsFeedback: {
    event: constants.GET_TRENDS_FEEDBACK_CHARTS_EXPORT_DATA,
    statusStore: constants.TRENDS_FEEDBACK_CHARTS_EXPORT_STATUS,
    pathPrefix: constants.TRENDS_FEEDBACK_CHARTS_EXPORT_PATH,
    pathParamKeys: ['type', 'feedback'],
    pathParamAllowlists: {
      feedback: TRENDS_FEEDBACK_VALUES
    },
    stripKeys: ['type', 'feedback'],
    filenameSlug: 'trends-feedback'
  },
  trendsBlocked: {
    event: constants.GET_TRENDS_BLOCKED_CHARTS_EXPORT_DATA,
    statusStore: constants.TRENDS_BLOCKED_CHARTS_EXPORT_STATUS,
    pathPrefix: constants.TRENDS_BLOCKED_CHARTS_EXPORT_PATH,
    pathParamKeys: ['category'],
    pathParamAllowlists: {
      category: TRENDS_BLOCKED_CATEGORIES
    },
    stripKeys: ['category'],
    filenameSlug: (payload) => `trends-blocked-${payload?.category || 'export'}`
  }
});

export const isTrendsPathExportModule = (exportModule) =>
  exportModule === constants.AUDIT_EXPORT_MODULE.TRENDS_FEEDBACK ||
  exportModule === constants.AUDIT_EXPORT_MODULE.TRENDS_BLOCKED;

export const getAuditExportStatusStoreKey = (exportModule) =>
  AUDIT_EXPORT_MODULE_CONFIG[exportModule]?.statusStore || constants.OBSERVE_EXPORT_STATUS;

export const getTrendsExportPathParams = (exportModule, payload = {}) => {
  const config = AUDIT_EXPORT_MODULE_CONFIG[exportModule];

  if (!config?.pathParamKeys) {
    return {};
  }

  return config.pathParamKeys.reduce((acc, key) => {
    acc[key] = payload[key];
    return acc;
  }, {});
};

export const hasValidTrendsExportPathParams = (exportModule, payload = {}) => {
  const config = AUDIT_EXPORT_MODULE_CONFIG[exportModule];

  if (!config?.pathParamKeys?.length) {
    return true;
  }

  const pathParams = getTrendsExportPathParams(exportModule, payload);

  return config.pathParamKeys.every((key) => isAllowedPathParam(pathParams[key], config.pathParamAllowlists?.[key]));
};
